import { useEffect, useMemo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MultiBackend, createTransition } from "react-dnd-multi-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { HiOutlineDotsVertical } from "react-icons/hi";

/* =========================================================
   CONFIG
========================================================= */

const ItemTypes = { TOKEN: "TOKEN" };

const TouchTransition = createTransition("touchstart", (event) => {
  return event.touches != null;
});

const HTML5toTouchBackends = {
  backends: [
    { id: "html5", backend: HTML5Backend },
    {
      id: "touch",
      backend: TouchBackend,
      transition: TouchTransition,
      options: {
        enableMouseEvents: true,
        pressDelay: 120,
      },
    },
  ],
};

/* =========================================================
   HELPERS
========================================================= */

function shuffleArray(array) {
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

function normalizeMatchingQuestion(q) {
  const left = [];
  const right = [];

  q?.options?.forEach((opt, index) => {
    // LEFT SIDE
    if (opt.option1) {
      left.push({
        id: `left-${opt.id}`,
        optionId: opt.id,
        type: "text",
        value: opt.option1,
        answerId: opt.answer?.id,
      });
    }

    if (opt.image1) {
      left.push({
        id: `left-img-${opt.id}`,
        optionId: opt.id,
        type: "image",
        value: opt.image1,
        answerId: opt.answer?.id,
      });
    }

    // RIGHT SIDE (NO UNIQUE FILTER)
    if (opt.answer) {
      right.push({
        id: `answer-${opt.answer.id}-${opt.id}`, // IMPORTANT: unique id
        answerId: opt.answer.id,
        type: "text",
        value: opt.answer.answer,
      });
    }
  });

  return { left, right };
}

function renderToken(token) {
  if (!token) return null;

  if (token.type === "image")
    return <img src={token.value} alt="" draggable={false} className="h-24 w-24 object-contain" />;

  return <span className="font-semibold">{token.value}</span>;
}
function QuestionDropTarget({ leftItem, onDropToken, isCompleted }) {
  const [, drop] = useDrop({
    accept: ItemTypes.TOKEN,
    canDrop: () => !isCompleted,
    drop: (item) => onDropToken(item.token, leftItem),
  });

  return (
    <div ref={drop} className="h-full w-full">
      {renderToken(leftItem)}
    </div>
  );
}

/* =========================================================
   DRAGGABLE TOKEN
========================================================= */

function DraggableToken({ token, disabled, onReturn }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TOKEN,
    item: { token },
    canDrag: !disabled,
    end: (item, monitor) => {
      if (!monitor.didDrop() && onReturn) {
        // Token released outside → return to pool
        onReturn(item.token);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={!disabled ? drag : null}
      onContextMenu={(e) => e.preventDefault()}
      onTouchStart={(e) => e.preventDefault()}
      className={`flex items-center gap-3 rounded-md p-3.5 pl-[1.75rem] text-xs text-white shadow transition sm:text-base ${
        disabled ? "cursor-default bg-gray-400" : "cursor-move bg-purple-600 hover:bg-purple-700"
      } ${isDragging ? "opacity-50" : ""}`}
      style={{ userSelect: "none" }}
    >
      <span className="flex items-center">
        <HiOutlineDotsVertical className="-mx-1.5 h-5 w-5" />
        <HiOutlineDotsVertical className="-mx-2 h-5 w-5" />
      </span>

      {renderToken(token)}
    </div>
  );
}

/* =========================================================
   DROP ZONE
========================================================= */

function DropZone({ leftItem, filled, onDropToken, disabled, onReturnToPool }) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TOKEN,
    canDrop: () => !disabled,
    drop: (item) => onDropToken(item.token, leftItem),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={!disabled ? drop : null}
      className={`flex ${filled ? "min-h-[60px] p-2" : "min-h-[70px] p-6"} items-center justify-center ${
        !disabled && isOver ? "border-2 border-dashed border-gray-400 bg-gray-200" : ""
      } `}
    >
      {filled && (
        <DraggableToken
          token={filled}
          disabled={disabled}
          onReturn={() => {
            if (onReturnToPool) onReturnToPool(filled);
          }}
        />
      )}
    </div>
  );
}

/* =========================================================
   MAIN CONTENT
========================================================= */

function MatchingQuestionContent({ question, value = {}, onChange, isCompleted }) {
  const safeValue = value || {};

  const normalized = useMemo(() => {
    const data = normalizeMatchingQuestion(question);

    return {
      left: data.left,
      right: shuffleArray(data.right), // 👈 shuffled once
    };
  }, [question]);

  /* ================= AUTO SOLVE ================= */

  useEffect(() => {
    if (!isCompleted) return;

    const solved = {};

    normalized.left.forEach((leftItem) => {
      const correctAnswer = normalized.right.find((r) => r.answerId === leftItem.answerId);

      if (correctAnswer) {
        solved[leftItem.id] = {
          ...correctAnswer,
          isCorrect: true,
        };
      }
    });

    onChange?.(solved);
  }, [isCompleted, normalized]);

  /* ================= HANDLE DROP ================= */

  const handleDrop = (dragged, leftItem) => {
    if (isCompleted) return;

    const updatedValue = { ...safeValue };

    let sourceZoneKey = null;

    // 1️⃣ Find where dragged token currently exists
    Object.keys(updatedValue).forEach((key) => {
      if (updatedValue[key]?.id === dragged.id) {
        sourceZoneKey = key;
      }
    });

    const targetZoneKey = leftItem.id;
    const targetExisting = updatedValue[targetZoneKey];

    // 2️⃣ If dragging from another zone
    if (sourceZoneKey) {
      // 🔁 If target has something → SWAP
      if (targetExisting) {
        updatedValue[sourceZoneKey] = targetExisting;
      } else {
        delete updatedValue[sourceZoneKey];
      }
    }

    // 3️⃣ Place dragged into target
    updatedValue[targetZoneKey] = {
      ...dragged,
      isCorrect: dragged.answerId === leftItem.answerId,
    };

    onChange?.(updatedValue);
  };

  const placedIds = Object.values(safeValue).map((v) => v.id);

  const availableRightTokens = normalized.right.filter((item) => !placedIds.includes(item.id));

  /* ================= UI ================= */

  return (
    <div className="space-y-2 sm:space-y-8">
      {/* RIGHT TOKENS */}

      {!isCompleted && (
        <div
          className="flex min-h-[60px] flex-wrap gap-2 border border-dashed border-gray-400 bg-gray-50 p-2"
          onDrop={(e) => e.preventDefault()}
        >
          {availableRightTokens.map((token) => (
            <DraggableToken key={token.id} token={token} disabled={isCompleted} />
          ))}
        </div>
      )}

      {/* LEFT DROP ZONES */}
      <div className="grid grid-cols-1 border">
        {/* HEADER */}
        <div className="grid grid-cols-[1fr_1fr] bg-gray-200 font-semibold uppercase tracking-wide text-gray-700">
          <div className="border-l border-black/20 p-4 text-center">الاسئلة</div>
          <div className="p-4 text-center">الاجابات</div>
        </div>{" "}
        {normalized.left.map((leftItem, index) => {
          const filled = safeValue[leftItem.id];
          const rowBg = index % 2 === 0 ? "bg-white" : "bg-gray-100";

          return (
            <div key={leftItem.id} className={`grid grid-cols-[1fr_1fr] items-center ${rowBg}`}>
              {/* LEFT ITEM */}
              <div className="border-l border-black/20 p-2 text-xs font-medium sm:p-6 sm:text-base">
                <QuestionDropTarget leftItem={leftItem} onDropToken={handleDrop} isCompleted={isCompleted} />
              </div>
              {/* DROP ZONE */}
              <DropZone
                leftItem={leftItem}
                filled={filled}
                onDropToken={handleDrop}
                disabled={isCompleted}
                onReturnToPool={(token) => {
                  const updatedValue = { ...safeValue };
                  Object.keys(updatedValue).forEach((key) => {
                    if (updatedValue[key]?.id === token.id) {
                      delete updatedValue[key]; // remove from drop zone → returns to pool
                    }
                  });
                  onChange?.(updatedValue);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================
   WRAPPER
========================================================= */

export default function MatchingQuestion(props) {
  return (
    <DndProvider backend={MultiBackend} options={HTML5toTouchBackends}>
      <MatchingQuestionContent {...props} />
    </DndProvider>
  );
}
